import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ScenarioForm } from "@/components/scenarios/ScenarioForm";
import { RouteLoading, RouteError } from "@/components/layout/RouteStatus";
import { powtoonBE } from "@/lib/powtoon-be/client";
import { saveScenarioWithContext } from "@/lib/scenarios/saveScenario";
import {
  readQuestionnaireMCQ,
  type Scenario,
  type ScenarioSourceItem,
} from "@/lib/powtoon-be/types";
import { toast } from "sonner";
import { trackEvent } from "@/services/analytics";
import { ANALYTICS_CATEGORIES, ANALYTICS_ACTIONS } from "@/config/analytics-config";
import { activeQuestionnaireId } from "@/lib/scenarios/questionnaireIds";
import { contentForDuplicate, inputsOf } from "@/lib/scenarios/providers/spec";

function nextCopyName(base: string, existing: string[]): string {
  const stripped = base.replace(/\s*\(copy(?:\s+\d+)?\)\s*$/i, "").trim() || base;
  const lower = new Set(existing.map((n) => n.toLowerCase()));
  let candidate = `${stripped} (copy)`;
  let i = 2;
  while (lower.has(candidate.toLowerCase())) {
    candidate = `${stripped} (copy ${i})`;
    i += 1;
  }
  return candidate;
}

function buildDuplicateSeed(source: Scenario, allScenarios: Scenario[]): Scenario {
  return {
    ...source,
    name: nextCopyName(source.name, allScenarios.map((s) => s.name)),
    thumb_url: source.thumb_url,
    // Provider-agnostic: drops provisioned ids + questionnaire history for
    // whichever provider block this scenario uses.
    content: contentForDuplicate(source.content),
  };
}

export function TrainerNewScenario() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [params] = useSearchParams();
  const from = params.get("from") ?? undefined;

  const sourceQuery = useQuery({
    queryKey: ["scenario", from],
    queryFn: () => powtoonBE.scenarios.get(from!),
    enabled: !!from,
  });
  const listQuery = useQuery({
    queryKey: ["scenarios"],
    queryFn: () => powtoonBE.scenarios.list(),
    enabled: !!from,
  });
  const sourceQuestionnaireId = activeQuestionnaireId(sourceQuery.data);
  const sourceQuestionnaireQuery = useQuery({
    queryKey: ["questionnaire", from, sourceQuestionnaireId],
    queryFn: () =>
      powtoonBE.questionnaires.get(from!, sourceQuestionnaireId!),
    enabled: !!from && !!sourceQuestionnaireId,
  });

  if (from && (sourceQuery.isLoading || listQuery.isLoading)) return <RouteLoading />;
  if (from && sourceQuestionnaireId && sourceQuestionnaireQuery.isLoading) return <RouteLoading />;
  if (from && sourceQuery.error) return <RouteError message="Failed to load source scenario." />;

  const seed = from && sourceQuery.data
    ? buildDuplicateSeed(sourceQuery.data, listQuery.data ?? [])
    : undefined;

  const attachmentsToClone =
    from && sourceQuery.data
      ? {
          sourceScenarioId: from,
          objectIds: inputsOf(sourceQuery.data.content)
            .filter(
              (i): i is Extract<ScenarioSourceItem, { kind: "upload" }> =>
                i.kind === "upload",
            )
            .map((i) => i.object_id)
            .filter((id): id is string => !!id),
        }
      : undefined;

  return (
    <div className="p-8 max-w-4xl mx-auto">
      <h1 className="text-2xl font-semibold mb-6">Create new Live Avatar</h1>
      <ScenarioForm
        initial={seed}
        initialQuestionnaire={
          sourceQuestionnaireQuery.data ? readQuestionnaireMCQ(sourceQuestionnaireQuery.data) : null
        }
        submitLabel={from ? "Create copy" : "Create"}
        onCancel={() => navigate("/trainer")}
        onSubmit={async (data, meta) => {
          const created = await saveScenarioWithContext(
            data,
            {
              ...meta,
              attachmentsToClone:
                attachmentsToClone && attachmentsToClone.objectIds.length > 0
                  ? attachmentsToClone
                  : undefined,
            },
            { kind: "create" },
          );
          trackEvent({
            category: ANALYTICS_CATEGORIES.SCENARIO,
            action: ANALYTICS_ACTIONS.SUCCESS,
            ga4EventName: from ? "scenario_duplicated" : "scenario_created",
            extraData: { scenario_id: created.id, source_id: from ?? null },
          });
          if (from && sourceQuery.data?.thumb_url) {
            try {
              await powtoonBE.scenarios.setThumbnailFromUrl(
                created.id,
                sourceQuery.data.thumb_url,
              );
            } catch (err) {
              console.error(err);
              toast.warning("Duplicated — thumbnail copy failed");
            }
          }
          await qc.invalidateQueries({ queryKey: ["scenarios"] });
          toast.success(from ? "Scenario duplicated" : "Scenario created");
          navigate("/trainer");
        }}
      />
    </div>
  );
}